import { useState, useEffect, useCallback } from 'react';
import type { AppPortal, RoleSelection, RoleEnum } from '~/types';
import { isCurrentYear } from '~/utils/schoolYearState';

/** Staff roles, most capable first. */
const STAFF_ROLES = ['PRIMARY_ADMIN', 'ADMIN', 'TEACHER'] as const;
const FAMILY_ROLE = 'PRIMARY_FAMILY_MEMBER';

export function staffRoleIn(roles: readonly string[]): string | null {
  return STAFF_ROLES.find((role) => roles.includes(role)) ?? null;
}

export function familyRoleIn(roles: readonly string[]): string | null {
  return roles.includes(FAMILY_ROLE) ? FAMILY_ROLE : null;
}

/**
 * Which role the app presents someone as.
 *
 * Staff roles are not a choice. Somebody who administers the school and also
 * teaches a class is one person with one job, and their way into the teaching
 * views is the Teaching link rather than a mode they switch into — so they are
 * always resolved to the most capable staff role they hold. That also means a
 * role stored from the days of the sidebar switcher cannot strand an
 * administrator in a teacher's sidebar with no way back.
 *
 * Being a parent is different: their children's pages are a different product,
 * not a smaller version of this one. So a saved choice to be there is honoured.
 */
export function resolveRole(roles: readonly string[], savedRole: string | null): string | null {
  const family = familyRoleIn(roles);
  if (family && savedRole === family) return family;
  return staffRoleIn(roles) ?? family ?? roles[0] ?? null;
}

const STORAGE_KEYS = {
  schoolId: 'solastack.selectedSchoolId',
  role: 'solastack.selectedRole',
  schoolYearId: 'solastack.selectedSchoolYearId',
} as const;

export function useRoleSelection(bootstrapData: AppPortal | undefined | null, schoolSlug: string | null = null, yearSlug: string | null = null) {
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [selectedSchoolYearId, setSelectedSchoolYearId] = useState<string | null>(null);

  // Load from URL slug first, then localStorage, then defaults
  useEffect(() => {
    if (bootstrapData?.accessibleSchools) {
      let targetSchool = null;

      // Priority 1: Use school from URL slug if provided
      if (schoolSlug) {
        targetSchool = bootstrapData.accessibleSchools.find(
          as => as.school.slug === schoolSlug
        );
      }

      // Priority 2: Use saved school from localStorage
      if (!targetSchool) {
        const savedSchoolId = localStorage.getItem(STORAGE_KEYS.schoolId);
        targetSchool = bootstrapData.accessibleSchools.find(
          as => as.school.id === savedSchoolId
        );
      }

      // Priority 3: Use first school as default
      if (!targetSchool) {
        targetSchool = bootstrapData.accessibleSchools[0];
      }

      if (targetSchool) {
        const savedRole = localStorage.getItem(STORAGE_KEYS.role);
        const savedSchoolYearId = localStorage.getItem(STORAGE_KEYS.schoolYearId);

        const validRole = resolveRole(targetSchool.roles, savedRole);
        setSelectedSchoolId(targetSchool.school.id);
        setSelectedRole(validRole);

        // Update localStorage with the selected school (from URL or fallback)
        localStorage.setItem(STORAGE_KEYS.schoolId, targetSchool.school.id);

        // Resolve the school year. Priority: URL yearSlug > saved (localStorage) > first
        // accessible year. Reading the URL first keeps a refresh on a year-scoped page
        // (years/:yearSlug/...) pinned to that year instead of snapping to a stale default.
        const isAccessible = (year: typeof targetSchool.accessibleSchoolYears[number] | undefined) =>
          !!year && year.roles.includes(validRole as RoleEnum);

        let resolvedYearId: string | null = null;

        if (yearSlug) {
          const urlYear = targetSchool.accessibleSchoolYears.find(
            asy => asy.schoolYear.slug === yearSlug && asy.roles.includes(validRole as RoleEnum)
          );
          if (urlYear) resolvedYearId = urlYear.schoolYear.id;
        }

        if (!resolvedYearId && savedSchoolYearId) {
          const savedYear = targetSchool.accessibleSchoolYears.find(
            asy => asy.schoolYear.id === savedSchoolYearId
          );
          if (isAccessible(savedYear)) resolvedYearId = savedSchoolYearId;
        }

        // Default to the year the school is actually running (state ACTIVE), so a
        // fresh login lands on this year's work rather than whichever year was
        // created first. Fall back to the first accessible year when no year is
        // marked active (e.g. a school still in setup).
        if (!resolvedYearId) {
          const accessibleYears = targetSchool.accessibleSchoolYears.filter(asy => asy.roles.includes(validRole as RoleEnum));
          const defaultYear = accessibleYears.find(asy => isCurrentYear(asy.schoolYear)) ?? accessibleYears[0];
          resolvedYearId = defaultYear ? defaultYear.schoolYear.id : null;
        }

        setSelectedSchoolYearId(resolvedYearId);
      }
    }
  }, [bootstrapData, schoolSlug, yearSlug]);

  // Save to localStorage when selection changes
  useEffect(() => {
    if (selectedSchoolId) {
      localStorage.setItem(STORAGE_KEYS.schoolId, selectedSchoolId);
    }
  }, [selectedSchoolId]);

  useEffect(() => {
    if (selectedRole) {
      localStorage.setItem(STORAGE_KEYS.role, selectedRole);
    }
  }, [selectedRole]);

  useEffect(() => {
    // Only persist a real selection. Do NOT removeItem on a null value: on mount
    // selectedSchoolYearId is null before the async bootstrap arrives, and clearing
    // here would wipe the saved year before the load effect can restore it — which
    // made a refresh snap back to the default year.
    if (selectedSchoolYearId) {
      localStorage.setItem(STORAGE_KEYS.schoolYearId, selectedSchoolYearId);
    }
  }, [selectedSchoolYearId]);

  // Set selection function
  const setSelection = useCallback(({ schoolId, role, schoolYearId }: RoleSelection) => {
    if (schoolId) setSelectedSchoolId(schoolId);
    if (role) setSelectedRole(role);
    if (schoolYearId !== undefined) setSelectedSchoolYearId(schoolYearId ?? null);
  }, []);

  return {
    selectedSchoolId,
    selectedRole,
    selectedSchoolYearId,
    setSelection,
  };
}
